import React from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { StatusBadge } from './PosBits';
import { PosTable } from './PosTable';
import { inr, monthLabel, num, usePosGet } from './posUtils';

const Stat = ({ l, v }) => (
  <div className="bg-slate-50 rounded-lg p-2.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{l}</div><div className="font-bold tabular-nums">{v}</div></div>
);

const Body = ({ id }) => {
  const { data: d } = usePosGet(`customers/${encodeURIComponent(id)}`, {});
  if (!d) return <p className="text-sm text-slate-400 mt-6">Loading…</p>;
  const act = (fn) => (v, r) => (r.active ? fn(v) : <span className="text-slate-400 italic">{r.has_data ? 'No purchase' : 'Not imported'}</span>);
  return (
    <div className="space-y-4 mt-4" data-testid="customer-detail">
      <div className="flex items-center gap-2 text-sm text-slate-600">
        <span>#{d.customer_id}</span><span>·</span><span>{d.mobile}</span><StatusBadge s={d.status} />
      </div>
      {(d.names_seen.length > 1 || d.mobiles_seen.length > 1) && (
        <p className="text-xs text-amber-700">Also recorded as: {[...d.names_seen, ...d.mobiles_seen].join(', ')}</p>
      )}
      <div className="grid grid-cols-3 gap-2">
        <Stat l="Total Sales" v={inr(d.total_sales)} /><Stat l="Total Orders" v={num(d.total_orders)} /><Stat l="Avg Monthly" v={inr(d.avg_monthly_sales)} />
        <Stat l="Pts Earned" v={num(d.points_earned)} /><Stat l="Pts Burned" v={num(d.points_burned)} /><Stat l="Net Pts" v={num(d.net_points)} />
        <Stat l="First Purchase" v={monthLabel(d.first_month)} /><Stat l="Last Purchase" v={monthLabel(d.last_month)} /><Stat l="Active Months" v={d.active_months} />
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={d.history.map((h) => ({ ...h, label: monthLabel(h.month) }))}>
          <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="label" fontSize={10} /><YAxis fontSize={10} /><Tooltip formatter={(v) => inr(v)} />
          <Bar dataKey="sales" name="Sales" fill="#059669" radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
      <PosTable testid="customer-history-table" rows={[...d.history].reverse()} columns={[
        { key: 'month', label: 'Month', render: monthLabel },
        { key: 'orders', label: 'Orders', align: 'right', render: act(num) },
        { key: 'sales', label: 'Sales', align: 'right', render: act(inr) },
        { key: 'points_earned', label: 'Earned', align: 'right', render: act(num) },
        { key: 'points_burned', label: 'Burned', align: 'right', render: act(num) },
        { key: 'net_points', label: 'Net', align: 'right', render: act(num) },
      ]} />
    </div>
  );
};

export const CustomerSheet = ({ id, onClose }) => (
  <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
    <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="customer-sheet">
      <SheetHeader><SheetTitle>Customer History</SheetTitle></SheetHeader>
      {id && <Body id={id} />}
    </SheetContent>
  </Sheet>
);
